import { useState, useEffect } from "react";
import { Image, Pagination, Tooltip } from "antd";
import Helper from "../../utils/Helper";
import { INVENTORY_INGREDIENT_UNIT, PAGINATION_PAGE_OPTIONS } from "../../constant/constants";

export default function InventoryTable({
  data,
  coloredCols = [],
  handleView = () => {},
  handleEdit = () => {},
  handleDelete = () => {},
  handleAcitve = () => {},
  showViewButton = false,
  showEditButton = false,
  showActionButtons = true,
  showBottomTotal = false,
  showActiveButton = false,
  bottomTotalValue = 0,
  paginationRecords,
}) {
  const { handlePageChange, page, perPage, setPerPage, total } = paginationRecords;
  const [tableCols, setTableCols] = useState([]);

  /** init report table */
  useEffect(() => {
    if (data == null) return;
    if (!Array.isArray(data) && data.length < 1) {
      return;
    }
    // set the table column names
    setTableCols(getTableColumns(data, showActionButtons));
  }, [data]);

  function getTableColumns(data, showActionButtons) {
    if (data.length < 1) {
      return;
    }

    let cols = Object.keys(data[0]);
    for (let i = 0; i < cols.length; i++) {
      let tempColName = cols[i].replace(/([A-Z])/g, " $1").trim();
      cols[i] = tempColName.charAt(0).toUpperCase() + tempColName.slice(1);
      if (cols[i].length <= 3) {
        cols[i] = cols[i].toUpperCase();
      }
    }
    /**
     * Here
     * !["id", "extra", 'status'] => this means column will not show in table
     */
    cols = ["SN", ...cols, "Action"].filter((col) => !["id", "extra", "status"].includes(col.toLowerCase()));
    if (!showActionButtons) {
      cols = cols.filter((col) => col !== "Action");
    }

    return cols;
  }

  function getUnitLabel(val) {
    return INVENTORY_INGREDIENT_UNIT.find((item) => item.value === val).label || "";
  }

  return (
    <div>
      <table className="w-full report-table bg-[#FAFAFA]">
        <thead className="h-[56px] bg-[#EFEFEF] border border-px border-[#E9EBEF] font-p_semibold text-xs">
          <tr>
            {tableCols.map((col, idx) => (
              <th key={idx} className={`${coloredCols?.includes(col) && "text-[#341557] break-words break-all whitespace-normal"}`}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-center font-p_medium text-xs">
          {data?.map((item, idx) => (
            <tr key={idx} className={`${item.extra ? item.extra.bgColorClass : "bg-white"}`}>
              <td
                className="border-t border-t-[1px] border-t-[#E9EBEF]
                  border-b border-b-[1px] border-b-[#E9EBEF] 
                  border-l border-l-[1px] border-l-[#E9EBEF] h-[56px]"
              >
                {page === 1 ? idx + 1 : perPage * (page - 1) + idx + 1}
              </td>
              {Object.entries(item)?.map(([key, value], idx) => {
                /** Removed columns data will be hide in the table */
                if (["id", "extra", "status"].includes(key.toLowerCase())) return null;
                return (
                  <td
                    key={idx}
                    className={`
                    text-[12px] font-p_medium leading-[18px]
                    border-t border-t-[1px] border-t-[#E9EBEF]
                    border-b border-b-[1px] border-b-[#E9EBEF]
                    whitespace-normal break-words break-all
                    ${key === "recipeName" || "name" ? "max-w-[200px] px-1" : ""}
                    ${key === "category" || "address" ? "max-w-[150px] px-1" : ""}
                  `}
                  >
                    {Helper.isFloat(value)
                      ? value.toFixed(2)
                      : key === "unit"
                      ? getUnitLabel(value)
                      : ["System Default", "Default"].includes(value)
                      ? ""
                      : value}
                  </td>
                );
              })}

              {showActionButtons ? (
                <td
                  className="flex items-center justify-center border-t border-t-[1px] border-t-[#E9EBEF]
                  border-b border-b-[1px] border-b-[#E9EBEF]
                  border-r border-r-[1px] border-r-[#E9EBEF] h-[56px] space-x-2"
                >
                  {showActiveButton && (
                    <button
                      onClick={() => handleAcitve(item.id, item.recipeType, item.status)}
                      className="w-[43px] h-[43px]"
                    >
                      {item.status === "Active" ? (
                        <Tooltip placement="leftTop" title="Deactivate Recipe?">
                          <Image src="/images/tickicon.svg" preview={false} />
                        </Tooltip>
                      ) : (
                        <Tooltip placement="leftTop" title="Activate Recipe?">
                          <Image src="/images/tickicongrey.svg" preview={false} />
                        </Tooltip>
                      )}
                    </button>
                  )}
                  {showViewButton && (
                    <button onClick={() => handleView(item.id, item.recipeType)} className="w-[38px] h-[38px]">
                      <Image src="/images/icons/view.svg" preview={false} />
                    </button>
                  )}
                  {showEditButton && (
                    <button
                      onClick={() => handleEdit(item.id, item.recipeType, item.variant)}
                      className="w-[38px] h-[38px]"
                    >
                      <Image
                        src="https://tabletap-cdn.s3.ap-south-1.amazonaws.com/tabletap-prod/static-assets/edit_user_icon.svg"
                        preview={false}
                      />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(item.id, item.recipeType)}
                    className="w-[38px] h-[38px] bg-[#FCBD10] rounded-full flex items-center justify-center"
                  >
                    <Image src="/images/icons/delete.svg" preview={false} />
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>

        {showBottomTotal && (
          <tfoot>
            <tr>
              <td rowSpan={3} colSpan={Object.keys(data[0]).length + 1}>
                <div className="bg-[#EFEFEF] text-right h-[56px] pr-[42px] font-p_medium text-[16px] text-black mt-[100px] flex items-center justify-end">
                  Total : {bottomTotalValue.toFixed(2)} TK
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>

      {/* Pagination */}
      <div className="mt-[100px] mb-[50px] report-pagination relative custom-pagination-options">
        <Pagination
          showSizeChanger
          onChange={handlePageChange}
          onShowSizeChange={(current, size) => {
            setPerPage(size);
          }}
          current={page}
          pageSize={perPage}
          total={total}
          pageSizeOptions={PAGINATION_PAGE_OPTIONS}
          locale={{ items_per_page: "" }}
          showQuickJumper={false}
        />

        <div
          className="absolute h-[48px] flex items-center space-x-[40px] 
          top-0 right-[122px] text-[18px] font-medium text-[#7D7D7D] mr-3"
        >
          <div>Total: {total}</div>
          <div>Row </div>
        </div>
      </div>
    </div>
  );
}
